import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ResponsesArtifact } from '../data/types';
import {
  decodeFilterUrl,
  EMPTY_FILTERS,
  encodeFilterUrl,
  normaliseFilters,
  type ComparisonState,
  type DashboardTab,
  type FilterState,
} from './filters';

type Section = 'story' | 'dashboard' | 'voices' | 'methodology';

interface ReportStateValue {
  section: Section;
  setSection(section: Section): void;
  tab: DashboardTab;
  setTab(tab: DashboardTab): void;
  filters: FilterState;
  setFilters(filters: FilterState): void;
  comparison: ComparisonState;
  setComparison(comparison: ComparisonState): void;
  mapLocation: string | undefined;
  setMapLocation(location: string | undefined): void;
  responses: ResponsesArtifact | null;
}

const ReportStateContext = createContext<ReportStateValue | null>(null);

const EMPTY_COMPARISON: ComparisonState = { enabled: false, baseline: EMPTY_FILTERS, comparison: EMPTY_FILTERS };

export function ReportStateProvider({ responses, children }: { responses: ResponsesArtifact | null; children: ReactNode }) {
  const initial = useMemo(() => decodeFilterUrl(window.location.search), []);
  const [section, setSectionState] = useState<Section>(() => window.location.hash === '#voices' ? 'voices' : window.location.hash === '#dashboard' ? 'dashboard' : window.location.hash === '#methodology' ? 'methodology' : 'story');
  const [tab, setTab] = useState<DashboardTab>(initial.tab);
  const [filters, setFiltersState] = useState<FilterState>(initial.filters);
  const [comparison, setComparison] = useState<ComparisonState>(initial.comparison ?? EMPTY_COMPARISON);
  const [mapLocation, setMapLocation] = useState<string | undefined>(initial.areaLocation);

  const setSection = useCallback((next: Section) => {
    setSectionState(next);
    requestAnimationFrame(() => document.getElementById(next)?.scrollIntoView({ block: 'start', behavior: 'smooth' }));
  }, []);
  const setFilters = useCallback((next: FilterState) => setFiltersState(normaliseFilters(next)), []);

  useEffect(() => {
    const params = encodeFilterUrl({ tab, filters, areaLocation: mapLocation, comparison });
    const hash = section === 'story' ? window.location.hash.match(/^#chapter-[1-6]$/)?.[0] ?? '' : `#${section}`;
    history.replaceState(null, '', `${window.location.pathname}?${params.toString()}${hash}`);
  }, [comparison, filters, mapLocation, section, tab]);

  const value = useMemo<ReportStateValue>(() => ({
    section, setSection, tab, setTab, filters, setFilters, comparison, setComparison,
    mapLocation, setMapLocation, responses,
  }), [comparison, filters, mapLocation, responses, section, setFilters, setSection, tab]);
  return <ReportStateContext.Provider value={value}>{children}</ReportStateContext.Provider>;
}

export function useReportState(): ReportStateValue {
  const value = useContext(ReportStateContext);
  if (!value) throw new Error('useReportState must be used inside ReportStateProvider');
  return value;
}
